import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Attribute } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteAttribute } from '../hooks/use-attribute-mutations';

export interface AttributeDeleteDialogProps {
  attribute: Attribute | null;
  onOpenChange: (open: boolean) => void;
  /** After the backend confirmed; the editor leaves the deleted attribute's page. */
  onDeleted?: () => void;
}

/** The backend refuses (409 RESOURCE_IN_USE) while a product type or product still uses it. */
export function AttributeDeleteDialog({
  attribute,
  onOpenChange,
  onDeleted,
}: AttributeDeleteDialogProps) {
  const { t } = useTranslation(['attributes', 'common', 'errors']);
  const remove = useDeleteAttribute();
  const message = useErrorMessage();
  const error = isApiError(remove.error) ? remove.error : null;

  return (
    <AlertDialog
      open={attribute !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('attributes:delete.title', { label: attribute?.label ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('attributes:delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {error?.code === 'RESOURCE_IN_USE'
                ? t('attributes:delete.inUse')
                : message(remove.error)}
              {error?.requestId ? (
                <span className="block text-xs">
                  {t('errors:requestIdShort', { requestId: error.requestId })}
                </span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (attribute) {
                // The detail page unmounts this dialog as soon as the row leaves the cache, and
                // per-call mutate callbacks are dropped on unmount; the promise still resolves.
                void remove.mutateAsync(attribute.id).then(
                  () => {
                    onOpenChange(false);
                    onDeleted?.();
                  },
                  () => undefined,
                );
              }
            }}
          >
            {remove.isPending ? t('attributes:delete.submitting') : t('attributes:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
